import { useState, useEffect } from 'react';
import { getWeeklyStats } from '../api';
import { useAuth } from '../contexts/AuthContext';

// Icons as inline SVGs
const Icons = {
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10"/>
      <line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  trendUp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
      <polyline points="17 6 23 6 23 12"/>
    </svg>
  ),
  trendDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
      <polyline points="17 18 23 18 23 12"/>
    </svg>
  ),
  trendSteady: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  trophy: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
      <path d="M4 22h16"/>
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </svg>
  ),
  lightbulb: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18h6"/>
      <path d="M10 22h4"/>
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
    </svg>
  ),
};

const WeeklySummary = () => {
  const { session } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);

  useEffect(() => {
    if (session) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = session?.access_token;
      if (!token) return;

      const data = await getWeeklyStats(token);
      setStats(data);
    } catch (err) {
      console.error('Error fetching weekly stats:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return Icons.trendUp;
      case 'declining':
        return Icons.trendDown;
      default:
        return Icons.trendSteady;
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'improving':
        return 'var(--green-500)';
      case 'declining':
        return 'var(--red-500)';
      default:
        return 'var(--text-tertiary)';
    }
  };

  const getTrendText = (trend) => {
    switch (trend) {
      case 'improving':
        return 'Improving';
      case 'declining':
        return 'Needs Attention';
      default:
        return 'Steady';
    }
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  const styles = {
    container: {
      background: 'var(--bg-card)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: 'var(--radius-xl)',
      border: '1px solid var(--border-subtle)',
      overflow: 'hidden',
    },
    header: {
      padding: 'var(--space-xl) var(--space-2xl)',
      borderBottom: '1px solid var(--border-subtle)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-md)',
    },
    headerIcon: {
      width: '48px',
      height: '48px',
      background: 'var(--gradient-primary)',
      borderRadius: 'var(--radius-lg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    },
    title: {
      fontFamily: 'var(--font-display)',
      fontSize: '1.5rem',
      fontWeight: 600,
      color: 'var(--text-primary)',
      margin: 0,
    },
    subtitle: {
      color: 'var(--text-tertiary)',
      fontSize: '0.9rem',
      marginTop: 'var(--space-xs)',
    },
    trendBadge: (trend) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-sm)',
      padding: 'var(--space-sm) var(--space-md)',
      background: trend === 'improving'
        ? 'rgba(34, 197, 94, 0.1)'
        : trend === 'declining'
        ? 'rgba(239, 68, 68, 0.1)'
        : 'var(--bg-surface)',
      border: `1px solid ${trend === 'improving'
        ? 'rgba(34, 197, 94, 0.3)'
        : trend === 'declining'
        ? 'rgba(239, 68, 68, 0.3)'
        : 'var(--border-subtle)'}`,
      borderRadius: 'var(--radius-md)',
    }),
    trendIcon: (trend) => ({
      width: '20px',
      height: '20px',
      color: getTrendColor(trend),
    }),
    trendText: (trend) => ({
      fontWeight: 600,
      fontSize: '0.9rem',
      color: getTrendColor(trend),
    }),
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 'var(--space-lg)',
      padding: 'var(--space-xl) var(--space-2xl)',
    },
    statCard: (variant) => {
      const gradients = {
        current: {
          bg: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(99, 102, 241, 0.05) 100%)',
          border: 'rgba(99, 102, 241, 0.3)',
          accent: 'var(--indigo-400)',
        },
        best: {
          bg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.05) 100%)',
          border: 'rgba(245, 158, 11, 0.3)',
          accent: 'var(--amber-400)',
        },
        total: {
          bg: 'linear-gradient(135deg, rgba(34, 211, 238, 0.15) 0%, rgba(34, 211, 238, 0.05) 100%)',
          border: 'rgba(34, 211, 238, 0.3)',
          accent: 'var(--cyan-400)',
        },
      };
      const colors = gradients[variant] || gradients.current;
      return {
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-lg)',
      };
    },
    statHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-sm)',
      marginBottom: 'var(--space-md)',
    },
    statIcon: (color) => ({
      width: '20px',
      height: '20px',
      color: color,
    }),
    statLabel: (color) => ({
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: color,
    }),
    statValue: {
      fontFamily: 'var(--font-display)',
      fontSize: '2rem',
      fontWeight: 700,
      color: 'var(--text-primary)',
      marginBottom: 'var(--space-xs)',
    },
    statSubtext: (color) => ({
      fontSize: '0.85rem',
      color: color,
      opacity: 0.8,
    }),
    chartSection: {
      padding: 'var(--space-xl) var(--space-2xl)',
      background: 'var(--bg-surface)',
      borderTop: '1px solid var(--border-subtle)',
    },
    chartHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-sm)',
      marginBottom: 'var(--space-lg)',
    },
    chartDot: {
      width: '8px',
      height: '8px',
      background: 'var(--indigo-500)',
      borderRadius: '50%',
    },
    chartTitle: {
      fontSize: '0.85rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: 'var(--text-secondary)',
    },
    barContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-md)',
    },
    barRow: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-sm)',
    },
    barLabel: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    barLabelLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-sm)',
    },
    barLabelText: (isActive) => ({
      fontSize: '0.9rem',
      fontWeight: 500,
      color: isActive ? 'var(--indigo-400)' : 'var(--text-secondary)',
    }),
    barStats: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-md)',
      fontSize: '0.8rem',
      color: 'var(--text-tertiary)',
    },
    barStatValue: {
      fontWeight: 600,
      color: 'var(--text-primary)',
    },
    barGoalValue: {
      fontWeight: 600,
      color: 'var(--green-500)',
    },
    barTrack: (isHovered) => ({
      position: 'relative',
      background: 'var(--bg-deep)',
      borderRadius: 'var(--radius-md)',
      height: '36px',
      overflow: 'hidden',
      border: `1px solid ${isHovered ? 'var(--border-medium)' : 'var(--border-subtle)'}`,
      transition: 'all var(--duration-fast)',
    }),
    barFill: (percentage, isActive) => ({
      height: '100%',
      width: `${Math.max(percentage, 5)}%`,
      background: isActive
        ? 'var(--gradient-primary)'
        : 'linear-gradient(90deg, rgba(99, 102, 241, 0.6), rgba(34, 211, 238, 0.6))',
      borderRadius: 'inherit',
      transition: 'width 0.6s var(--ease-out)',
      display: 'flex',
      alignItems: 'center',
      paddingLeft: 'var(--space-md)',
    }),
    barValue: {
      color: 'white',
      fontSize: '0.85rem',
      fontWeight: 600,
      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
    },
    activeDot: {
      width: '8px',
      height: '8px',
      background: 'var(--indigo-500)',
      borderRadius: '50%',
      animation: 'pulse 2s ease-in-out infinite',
    },
    insight: {
      padding: 'var(--space-xl) var(--space-2xl)',
      borderTop: '1px solid var(--border-subtle)',
    },
    insightCard: (trend) => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: 'var(--space-md)',
      padding: 'var(--space-lg)',
      background: trend === 'improving'
        ? 'rgba(34, 197, 94, 0.08)'
        : trend === 'declining'
        ? 'rgba(239, 68, 68, 0.08)'
        : 'var(--bg-surface)',
      border: `1px solid ${trend === 'improving'
        ? 'rgba(34, 197, 94, 0.2)'
        : trend === 'declining'
        ? 'rgba(239, 68, 68, 0.2)'
        : 'var(--border-subtle)'}`,
      borderRadius: 'var(--radius-lg)',
    }),
    insightIcon: (trend) => ({
      width: '24px',
      height: '24px',
      color: trend === 'improving'
        ? 'var(--green-500)'
        : trend === 'declining'
        ? 'var(--red-500)'
        : 'var(--amber-400)',
      flexShrink: 0,
      marginTop: '2px',
    }),
    insightTitle: {
      fontWeight: 600,
      fontSize: '0.9rem',
      color: 'var(--text-primary)',
      marginBottom: 'var(--space-xs)',
    },
    insightText: {
      fontSize: '0.9rem',
      color: 'var(--text-secondary)',
      lineHeight: 1.6,
    },
    loadingSkeleton: {
      background: 'var(--bg-card)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: 'var(--radius-xl)',
      border: '1px solid var(--border-subtle)',
      padding: 'var(--space-2xl)',
    },
    skeletonHeader: {
      height: '32px',
      background: 'var(--bg-surface)',
      borderRadius: 'var(--radius-md)',
      width: '40%',
      marginBottom: 'var(--space-lg)',
    },
    skeletonContent: {
      height: '200px',
      background: 'var(--bg-surface)',
      borderRadius: 'var(--radius-md)',
    },
  };

  if (loading) {
    return (
      <div style={styles.loadingSkeleton}>
        <div style={styles.skeletonHeader} />
        <div style={styles.skeletonContent} />
      </div>
    );
  }

  if (error || !stats) return null;

  const allWeeks = [stats.current_week, ...stats.last_4_weeks];
  const maxApps = Math.max(...allWeeks.map(w => w.applications_count), 1);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIcon}>
            <div style={{ width: '24px', height: '24px' }}>{Icons.chart}</div>
          </div>
          <div>
            <h3 style={styles.title}>Weekly Progress</h3>
            <p style={styles.subtitle}>Your performance over the last 5 weeks</p>
          </div>
        </div>
        <div style={styles.trendBadge(stats.trend)}>
          <div style={styles.trendIcon(stats.trend)}>
            {getTrendIcon(stats.trend)}
          </div>
          <span style={styles.trendText(stats.trend)}>
            {getTrendText(stats.trend)}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        {/* This Week */}
        <div style={styles.statCard('current')}>
          <div style={styles.statHeader}>
            <div style={styles.statIcon('var(--indigo-400)')}>
              {Icons.calendar}
            </div>
            <span style={styles.statLabel('var(--indigo-400)')}>This Week</span>
          </div>
          <div style={styles.statValue}>{stats.current_week.applications_count}</div>
          <div style={styles.statSubtext('var(--indigo-400)')}>
            {stats.current_week.average_per_day.toFixed(1)} apps/day avg
          </div>
        </div>

        {/* Best Week */}
        <div style={styles.statCard('best')}>
          <div style={styles.statHeader}>
            <div style={styles.statIcon('var(--amber-400)')}>
              {Icons.trophy}
            </div>
            <span style={styles.statLabel('var(--amber-400)')}>Best Week</span>
          </div>
          <div style={styles.statValue}>
            {stats.best_week ? stats.best_week.applications_count : 0}
          </div>
          <div style={styles.statSubtext('var(--amber-400)')}>
            {stats.best_week ? formatDateRange(stats.best_week.week_start, stats.best_week.week_end) : 'No data yet'}
          </div>
        </div>

        {/* All Time */}
        <div style={styles.statCard('total')}>
          <div style={styles.statHeader}>
            <div style={styles.statIcon('var(--cyan-400)')}>
              {Icons.target}
            </div>
            <span style={styles.statLabel('var(--cyan-400)')}>All Time</span>
          </div>
          <div style={styles.statValue}>{stats.total_applications}</div>
          <div style={styles.statSubtext('var(--cyan-400)')}>
            Total applications
          </div>
        </div>
      </div>

      {/* Bar Chart */}
      <div style={styles.chartSection}>
        <div style={styles.chartHeader}>
          <div style={styles.chartDot} />
          <span style={styles.chartTitle}>Application History</span>
        </div>
        <div style={styles.barContainer}>
          {allWeeks.map((week, index) => {
            const percentage = maxApps > 0 ? (week.applications_count / maxApps) * 100 : 0;
            const isCurrentWeek = index === 0;
            const isHovered = hoveredBar === index;

            return (
              <div
                key={index}
                style={styles.barRow}
                onMouseEnter={() => setHoveredBar(index)}
                onMouseLeave={() => setHoveredBar(null)}
              >
                <div style={styles.barLabel}>
                  <div style={styles.barLabelLeft}>
                    {isCurrentWeek && <div style={styles.activeDot} />}
                    <span style={styles.barLabelText(isCurrentWeek)}>
                      {isCurrentWeek ? 'This Week' : formatDateRange(week.week_start, week.week_end)}
                    </span>
                  </div>
                  <div style={styles.barStats}>
                    <span>
                      <span style={styles.barStatValue}>{week.applications_count}</span> apps
                    </span>
                    <span style={{ color: 'var(--border-medium)' }}>|</span>
                    <span>
                      <span style={styles.barGoalValue}>{week.goals_met_count}</span>/{week.total_days} goals
                    </span>
                  </div>
                </div>
                <div style={styles.barTrack(isHovered)}>
                  <div style={styles.barFill(percentage, isCurrentWeek)}>
                    {week.applications_count > 0 && (
                      <span style={styles.barValue}>{week.applications_count}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Insight */}
      <div style={styles.insight}>
        <div style={styles.insightCard(stats.trend)}>
          <div style={styles.insightIcon(stats.trend)}>
            {Icons.lightbulb}
          </div>
          <div>
            <div style={styles.insightTitle}>Insight</div>
            <p style={styles.insightText}>
              {stats.trend === 'improving' && "Excellent progress! Your application rate is trending upward. Keep this momentum going!"}
              {stats.trend === 'declining' && "Your activity has slowed down recently. Let's get back on track - even one application today helps!"}
              {stats.trend === 'stable' && "You're maintaining a consistent pace. Steady effort is the key to success!"}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.9); }
        }
      `}</style>
    </div>
  );
};

export default WeeklySummary;
